import { useEffect } from "react";
import { Eye, EyeOff } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScreenshotSvg } from "@/components/walkthrough/screenshot-svg";
import type { ScreenshotHighlight } from "@/components/walkthrough/screenshot-types";
import type { LiveStepDisplay } from "./live-walkthrough-screenshot";

interface LiveZoomDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  screenshotBlobUrl: string | null;
  displayedStep?: LiveStepDisplay;
  highlights: ScreenshotHighlight[];
  activeHighlightId?: string | null;
  showHighlights: boolean;
  onToggleHighlights: () => void;
  onSelectHighlight?: (id: string) => void;
  naturalSize?: { width: number; height: number } | null;
}

export function LiveZoomDialog({
  open,
  onOpenChange,
  screenshotBlobUrl,
  displayedStep,
  highlights,
  activeHighlightId,
  showHighlights,
  onToggleHighlights,
  onSelectHighlight,
  naturalSize,
}: LiveZoomDialogProps) {
  // Global keyboard shortcut (H) to toggle highlights while dialog is open
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        (e.target as HTMLElement)?.isContentEditable
      ) {
        return;
      }
      if (e.key === "h" || e.key === "H") {
        onToggleHighlights();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onToggleHighlights]);

  if (!screenshotBlobUrl) return null;

  const title = displayedStep
    ? `Step ${displayedStep.stepNumber}: ${displayedStep.title}`
    : "Screenshot inspection";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[88vh] max-h-[94vh] w-[95vw] max-w-7xl flex-col gap-3 p-4 sm:max-w-7xl">
        <DialogHeader className="border-b border-border/60 pb-2">
          <div className="flex items-center justify-between pr-8">
            <DialogTitle className="truncate text-sm font-semibold">
              {title}
            </DialogTitle>
            {highlights.length > 0 && (
              <button
                type="button"
                onClick={onToggleHighlights}
                className="inline-flex items-center gap-1.5 rounded bg-muted/80 px-2 py-1 text-xs font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring"
                title={
                  showHighlights
                    ? "Hide annotations (H)"
                    : "Show annotations (H)"
                }
                aria-label={
                  showHighlights ? "Hide annotations" : "Show annotations"
                }
              >
                {showHighlights ? (
                  <Eye className="h-3 w-3 text-primary" aria-hidden="true" />
                ) : (
                  <EyeOff
                    className="h-3 w-3 text-muted-foreground"
                    aria-hidden="true"
                  />
                )}
                <span>{showHighlights ? "Annotations on" : "Clean view"}</span>
                <kbd className="hidden sm:inline-block rounded border border-border bg-background px-1 font-mono text-xs text-muted-foreground">
                  H
                </kbd>
              </button>
            )}
          </div>
          <DialogDescription className="sr-only">
            Enlarged full-resolution screenshot with inspection highlights
          </DialogDescription>
        </DialogHeader>

        <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-auto rounded-lg bg-surface-tonal p-2">
          <ScreenshotSvg
            url={screenshotBlobUrl}
            alt={title}
            highlights={highlights}
            activeHighlightId={activeHighlightId}
            showHighlights={showHighlights}
            onSelectHighlight={onSelectHighlight}
            naturalSize={naturalSize}
            className="h-full w-full max-h-[80vh] rounded object-contain shadow-xs"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
